import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import { SegmentationLabel } from 'models/segmentation';
import {
  ScrollableList,
  SelectButtonsRow,
  InfoTextRow,
  CheckboxRow,
  CommentBox,
} from './RejectModal.styled';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (rejectedSegmentations: string[], comment: string) => void;
  segmentations: SegmentationLabel[];
  initialRejectedIds?: string[];
}

const RejectModal: FC<IProps> = props => {
  const { isModalOpen, handleCancel, handleOk, segmentations, initialRejectedIds = [] } = props;
  const [comment, setComment] = useState('');
  const [rejectedSegmentations, setRejectedSegmentations] = useState<Set<string>>(
    new Set(initialRejectedIds)
  );

  // Reset state when modal opens/closes or when initialRejectedIds changes
  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all items for faster technician reviews
      const allIds = segmentations.map(seg => seg.icid);
      setRejectedSegmentations(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialRejectedIds]);

  const handleCheckboxChange = (segmentationId: string, checked: boolean) => {
    setRejectedSegmentations(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(segmentationId);
      } else {
        newSet.delete(segmentationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = segmentations.map(seg => seg.icid);
    setRejectedSegmentations(new Set(allIds));
  };

  const handleSelectNone = () => {
    setRejectedSegmentations(new Set());
  };

  return (
    <Modal
      title="Select Segmentations to Reject"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <SelectButtonsRow>
            <Button size="small" onClick={handleSelectAll} style={{ marginRight: 8 }}>
              Select All
            </Button>
            <Button size="small" onClick={handleSelectNone}>
              Select None
            </Button>
          </SelectButtonsRow>
          <InfoTextRow>
            <Text type="secondary">
              Only selected segmentations will be rejected; others will remain unchanged.
            </Text>
          </InfoTextRow>
        </div>

        <ScrollableList>
          {segmentations.map(segmentation => {
            const isRejected = rejectedSegmentations.has(segmentation.icid);
            return (
              <CheckboxRow key={segmentation.icid}>
                <Checkbox
                  checked={isRejected}
                  onChange={e => handleCheckboxChange(segmentation.icid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{segmentation.segmentationClassValue}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {segmentation.icid}
                      {isRejected && ' (Currently Rejected)'}
                    </Text>
                  </Space>
                </Checkbox>
              </CheckboxRow>
            );
          })}
        </ScrollableList>

        <CommentBox>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment for the rejected segmentations..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </CommentBox>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(rejectedSegmentations), comment)}
            disabled={rejectedSegmentations.size === 0}
          >
            Reject ({rejectedSegmentations.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};
export default RejectModal;
